import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { IPagination, IValuation } from '@core/models';
import { City, Geomarker } from '@core/services';
import { lastValueFrom } from 'rxjs';
import { ValorationDetailItem } from '../../valorations-detail/valoration-detail-item/valoration-detail-item.component';

export interface IValorationsListData {
  city: number | null;
  marker: number | null;
  valuations: IValuation[];
  nextUrl?: string | null;
  total: number;
}

@Component({
  selector: 'app-valorations-list',
  imports: [MatDialogModule, TranslateModule, ValorationDetailItem],
  templateUrl: './valorations-list.component.html',
  styleUrl: './valorations-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValorationsList {
  private readonly cityService = inject(City);
  private readonly markerService = inject(Geomarker);

  readonly dialogRef = inject(MatDialogRef<ValorationsList>);
  readonly data = inject<IValorationsListData>(MAT_DIALOG_DATA);

  protected readonly valuations = signal<IValuation[]>(this.data.valuations ?? []);
  protected readonly nextUrl = signal<string | null>(this.data.nextUrl ?? null);
  protected readonly total = signal<number>(this.data.total || this.valuations().length);
  protected readonly loading = signal(false);

  constructor() {
    if (!this.valuations().length && this.total() > 0) {
      this.fetchValuations(undefined, true);
    }
  }

  protected async loadMore(): Promise<void> {
    const url = this.nextUrl();
    if (!url || this.loading()) return;

    await this.fetchValuations(url, false);
  }

  protected async refresh(): Promise<void> {
    await this.fetchValuations(undefined, true);
  }

  private async fetchValuations(url?: string, reset = false): Promise<void> {
    this.loading.set(true);
    try {
      const response = (this.data.city
        ? await lastValueFrom(this.cityService.getValuations(this.data.city, url))
        : await lastValueFrom(this.markerService.getValuations(this.data.marker!, url))) as IPagination<
        IValuation[]
      >;

      const current = reset ? [] : this.valuations();
      const merged = [...current, ...(response?.results ?? [])].filter(
        (item, index, all) => all.findIndex((candidate) => candidate.id === item.id) === index,
      );
      this.valuations.set(merged);
      this.nextUrl.set(response?.next ?? null);
      this.total.set(response?.count ?? merged.length);
    } catch (error) {
      console.error('Error fetching all valuations:', error);
    } finally {
      this.loading.set(false);
    }
  }
}
